import React, { useState, useEffect, useRef, useCallback } from 'react';
import useIsMobile from '../../hooks/useIsMobile';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { apiClient } from '../../services/authService';
import { useTranslation } from 'react-i18next';
import AddConsommableDialog from '../../components/AddConsommableDialog/AddConsommableDialog';
import ViewConsommableDialog from '../../components/ViewConsommableDialog/ViewConsommableDialog';
import AssignFormationsDialog from '../../components/AssignFormationsDialog/AssignFormationsDialog';
import {
    Package,
    Boxes,
    Plus,
    Search,
    Filter,
    Edit,
    Trash2,
    Truck,
    ClipboardList,
    CircleDollarSign,
    Eye,
    Link2,
    X
} from 'lucide-react';
import { formatConsommableRule, getDisplayType } from '../../utils/consommableQuantity';
import './ConsommablesPage.css';

const ConsommablesPage = () => {
    const { t, i18n } = useTranslation();
    const toast = useToast();
    const isMobile = useIsMobile();
    const [consommables, setConsommables] = useState([]);
    const [loading, setLoading] = useState(true);
    const { isAdmin, isResponsable, isLogistiqueCoordinator, isManager, user } = useAuth();
    const { can, canView, canCreate, canEdit, canDelete } = usePermissions();
    const [activeTab, setActiveTab] = useState('catalog');
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const searchTimeoutRef = useRef(null);
    const [filterCategory, setFilterCategory] = useState('all');
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [consommableToEdit, setConsommableToEdit] = useState(null);
    const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
    const [consommableToView, setConsommableToView] = useState(null);
    const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
    const [consommableToAssign, setConsommableToAssign] = useState(null);
    const [centres, setCentres] = useState([]);
    const [selectedCentre, setSelectedCentre] = useState('');
    const [stockRows, setStockRows] = useState([]);
    const [stockLoading, setStockLoading] = useState(false);
    const [stockDialogOpen, setStockDialogOpen] = useState(false);
    const [stockDialogMode, setStockDialogMode] = useState('SET');
    const [stockDialogRow, setStockDialogRow] = useState(null);
    const [stockQuantity, setStockQuantity] = useState('');
    const [stockThreshold, setStockThreshold] = useState('');
    const [stockComment, setStockComment] = useState('');
    const [stockSaving, setStockSaving] = useState(false);
    const [suppliers, setSuppliers] = useState([]);
    const [suppliersLoading, setSuppliersLoading] = useState(false);
    const [restockDialogOpen, setRestockDialogOpen] = useState(false);
    const [restockSupplier, setRestockSupplier] = useState('');
    const [restockLines, setRestockLines] = useState({});
    const [restockDeliveryDate, setRestockDeliveryDate] = useState('');
    const [restockComment, setRestockComment] = useState('');
    const [restockSaving, setRestockSaving] = useState(false);
    const [restockRequestsDialogOpen, setRestockRequestsDialogOpen] = useState(false);
    const [restockRequestsRow, setRestockRequestsRow] = useState(null);
    const [restockRequests, setRestockRequests] = useState([]);
    const [restockRequestsLoading, setRestockRequestsLoading] = useState(false);
    const [restockRequestsType, setRestockRequestsType] = useState('');
    const [restockRequestsStatus, setRestockRequestsStatus] = useState('');
    const [restockRequestsDateFrom, setRestockRequestsDateFrom] = useState('');
    const [restockRequestsDateTo, setRestockRequestsDateTo] = useState('');
    const [selectedRestockRequest, setSelectedRestockRequest] = useState(null);
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [stockCurrentPage, setStockCurrentPage] = useState(1);
    const pageSize = 10;

    useEffect(() => {
        fetchConsommables(currentPage);
    }, [currentPage, debouncedSearchTerm, filterCategory]);

    const canViewStock = user?.role !== 'FOURNISSEUR' && canView('stock');
    const canAddStock = canViewStock && canCreate('stock');
    const canSetStock = canViewStock && canEdit('stock');
    const canRemoveStock = canViewStock && canDelete('stock');
    const canRequestRestock = canViewStock && (
        can('stock', 'request')
        || canCreate('stock')
        || isResponsable
        || isLogistiqueCoordinator
        || isManager
        || isAdmin
    );
    const canEditStock = canSetStock || canRequestRestock;
    const canViewRestockRequests = canViewStock && canView('demandes');
    const canShowStockActions = canEditStock || canViewRestockRequests;

    useEffect(() => {
        fetchCentresForStock();
    }, [isAdmin, user]);

    useEffect(() => {
        if (activeTab === 'stock') {
            fetchStockRows();
            fetchSuppliersForRestock();
        }
    }, [activeTab, selectedCentre, debouncedSearchTerm]);

    useEffect(() => {
        if (!selectedCentre && centres.length === 1) {
            setSelectedCentre(String(centres[0].id));
        }
    }, [centres, selectedCentre]);

    const normalizeResults = (data) => {
        if (Array.isArray(data?.results)) return data.results;
        if (Array.isArray(data)) return data;
        return [];
    };

    const fetchCentresForStock = async () => {
        try {
            if (isAdmin || user?.all_centres) {
                const response = await apiClient.get('/centres/?page_size=500');
                const items = normalizeResults(response.data);
                setCentres(items);
                if (items.length === 1) setSelectedCentre(String(items[0].id));
                return;
            }

            const userCentres = Array.isArray(user?.centres_details)
                ? user.centres_details
                : (user?.centre_details ? [user.centre_details] : []);
            setCentres(userCentres);
            if (userCentres.length === 1) setSelectedCentre(String(userCentres[0].id));
        } catch (error) {
            console.error('Error fetching centres for stock:', error);
            setCentres([]);
        }
    };

    const fetchStockRows = async () => {
        if (!selectedCentre) {
            setStockRows([]);
            return;
        }

        try {
            setStockLoading(true);
            const params = new URLSearchParams();
            params.append('centre', selectedCentre);
            params.append('page_size', '500');
            if (debouncedSearchTerm) params.append('search', debouncedSearchTerm);

            const [consommableItems, stockItems] = await Promise.all([
                fetchAllPages(`/consommables/?${params.toString()}`),
                fetchAllPages(`/centre-stocks/?${params.toString()}`),
            ]);

            const stockByConsommable = new Map();
            stockItems.forEach((stock) => {
                if (stock?.consommable) stockByConsommable.set(String(stock.consommable), stock);
            });

            const rows = consommableItems.map((item) => {
                const stock = stockByConsommable.get(String(item.id));
                return {
                    id: stock?.id || `virtual-${selectedCentre}-${item.id}`,
                    stock_id: stock?.id || null,
                    centre: selectedCentre,
                    centre_name: centres.find((centre) => String(centre.id) === String(selectedCentre))?.name || '',
                    consommable: item.id,
                    consommable_name: item.name,
                    supplier: item.supplier,
                    supplier_name: item.supplier_details?.name || '-',
                    effective_devise: item.effective_devise || 'EUR',
                    quantity_available: stock?.quantity_available ?? 0,
                    minimum_threshold: stock?.minimum_threshold ?? 0,
                    needs_restock: stock ? stock.needs_restock : false,
                    updated_at: stock?.updated_at || null,
                };
            });
            setStockRows(rows);
        } catch (error) {
            console.error('Error fetching stock rows:', error);
            toast.error(t('stock.toasts.loadError'));
        } finally {
            setStockLoading(false);
        }
    };

    const fetchAllPages = async (initialUrl) => {
        const items = [];
        let nextUrl = initialUrl;
        let guard = 0;

        while (nextUrl && guard < 20) {
            guard += 1;
            const response = await apiClient.get(nextUrl);
            if (Array.isArray(response.data?.results)) {
                items.push(...response.data.results);
                if (response.data.next) {
                    const parsedNext = new URL(response.data.next, window.location.origin);
                    const nextPath = parsedNext.pathname.startsWith('/api/')
                        ? parsedNext.pathname.replace('/api', '')
                        : parsedNext.pathname;
                    nextUrl = `${nextPath}${parsedNext.search}`;
                } else {
                    nextUrl = null;
                }
            } else {
                items.push(...normalizeResults(response.data));
                nextUrl = null;
            }
        }

        return items;
    };

    const fetchSuppliersForRestock = async (force = false) => {
        if (!force && suppliers.length > 0) return;
        try {
            setSuppliersLoading(true);
            const response = await apiClient.get('/fournisseurs/all/');
            setSuppliers(normalizeResults(response.data));
        } catch (error) {
            console.error('Error fetching suppliers:', error);
            toast.error(t('stock.toasts.loadSuppliersError'));
        } finally {
            setSuppliersLoading(false);
        }
    };

    const fetchConsommables = async (page = 1) => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            params.append('page', page);
            params.append('page_size', pageSize);
            
            if (debouncedSearchTerm) {
                params.append('search', debouncedSearchTerm);
            }
            if (filterCategory !== 'all') {
                params.append('type', filterCategory);
            }
            
            const url = `/consommables/?${params.toString()}`;
            console.log('Fetching consommables:', url);
            
            const response = await apiClient.get(url);
            console.log('Response:', response.data);

            const dedupeById = (items) => {
                const map = new Map();
                (items || []).forEach(it => {
                    if (it && it.id) map.set(it.id, it);
                });
                return Array.from(map.values());
            };
            
            if (response.data.results) {
                setConsommables(dedupeById(response.data.results));
                setTotalCount(response.data.count);
                setTotalPages(Math.ceil(response.data.count / pageSize));
            } else {
                setConsommables(dedupeById(response.data || []));
                setTotalCount(response.data?.length || 0);
                setTotalPages(1);
            }
        } catch (error) {
            console.error('Error fetching consommables:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleConsommableAdded = () => {
        fetchConsommables(currentPage);
        setConsommableToEdit(null);
    };

    const executeSearch = useCallback(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        setDebouncedSearchTerm(searchTerm);
        setCurrentPage(1);
    }, [searchTerm]);

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        
        searchTimeoutRef.current = setTimeout(() => {
            setDebouncedSearchTerm(value);
            setCurrentPage(1);
        }, 3000);
    };

    const handleSearchKeyDown = (e) => {
        if (e.key === 'Enter') {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
            setDebouncedSearchTerm(searchTerm);
            setCurrentPage(1);
        }
    };

    const handleSearchBlur = () => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        if (searchTerm !== debouncedSearchTerm) {
            setDebouncedSearchTerm(searchTerm);
            setCurrentPage(1);
        }
    };

    const handleClearSearch = () => {
        setSearchTerm('');
        setDebouncedSearchTerm('');
        setCurrentPage(1);
    };

    const handleFilterChange = (e) => {
        setFilterCategory(e.target.value);
        setCurrentPage(1);
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setCurrentPage(1);
        setStockCurrentPage(1);
    };

    const handleOpenStockDialog = (row, mode) => {
        if ((mode === 'ADD' && !canAddStock) || (mode === 'SET' && !canSetStock) || (mode === 'REMOVE' && !canRemoveStock)) {
            toast.error(t('stock.toasts.permissionDenied'));
            return;
        }
        setStockDialogRow(row);
        setStockDialogMode(mode);
        setStockQuantity(mode === 'SET' ? String(row.quantity_available ?? 0) : '');
        setStockThreshold(String(row.minimum_threshold ?? 0));
        setStockComment('');
        setStockDialogOpen(true);
    };

    const handleCloseStockDialog = () => {
        setStockDialogOpen(false);
        setStockDialogRow(null);
        setStockQuantity('');
        setStockThreshold('');
        setStockComment('');
    };

    const handleSaveStockAdjustment = async (event) => {
        event.preventDefault();
        if (!stockDialogRow) return;

        const quantity = Number(stockQuantity);
        const minimumThreshold = Number(stockThreshold || 0);
        if (!Number.isInteger(quantity) || quantity < 0) {
            toast.error(t('stock.toasts.invalidQuantity'));
            return;
        }
        if (!Number.isInteger(minimumThreshold) || minimumThreshold < 0) {
            toast.error(t('stock.toasts.invalidThreshold'));
            return;
        }

        try {
            setStockSaving(true);
            await apiClient.post('/centre-stocks/adjust/', {
                centre: stockDialogRow.centre,
                consommable: stockDialogRow.consommable,
                mode: stockDialogMode,
                quantity,
                minimum_threshold: minimumThreshold,
                comment: stockComment,
            });
            toast.success(t('stock.toasts.updated'));
            handleCloseStockDialog();
            fetchStockRows();
        } catch (error) {
            console.error('Error saving stock adjustment:', error);
            const detail = error?.response?.data?.quantity?.[0]
                || error?.response?.data?.detail
                || error?.response?.data?.error
                || t('stock.toasts.updateError');
            toast.error(detail);
        } finally {
            setStockSaving(false);
        }
    };

    const handleOpenRestockDialog = (row = null) => {
        if (!canRequestRestock) {
            toast.error(t('stock.toasts.permissionDenied'));
            return;
        }
        setRestockSupplier(row?.supplier ? String(row.supplier) : '');
        setRestockLines(row?.consommable ? { [String(row.consommable)]: { selected: true, quantity: '' } } : {});
        setRestockDeliveryDate('');
        setRestockComment('');
        setRestockDialogOpen(true);
        fetchSuppliersForRestock(true);
        if (selectedCentre && stockRows.length === 0) {
            fetchStockRows();
        }
    };

    const handleCloseRestockDialog = () => {
        setRestockDialogOpen(false);
        setRestockSupplier('');
        setRestockLines({});
        setRestockDeliveryDate('');
        setRestockComment('');
    };

    const getRestockConsommableOptions = () => {
        return stockRows.filter((row) => !restockSupplier || String(row.supplier) === String(restockSupplier));
    };

    const updateRestockLine = (consommableId, patch) => {
        setRestockLines((prev) => {
            const current = prev[consommableId] || { selected: false, quantity: '' };
            return {
                ...prev,
                [consommableId]: { ...current, ...patch },
            };
        });
    };

    const handleSaveRestockRequest = async (event) => {
        event.preventDefault();
        if (!selectedCentre) {
            toast.error(t('stock.toasts.selectCenter'));
            return;
        }
        if (!restockSupplier || !restockDeliveryDate) {
            toast.error(t('stock.toasts.requiredRestock'));
            return;
        }

        const lines = Object.entries(restockLines)
            .filter(([, line]) => line?.selected)
            .map(([consommable, line]) => ({
                consommable,
                quantity: Number(line.quantity),
            }));

        if (!lines.length) {
            toast.error(t('stock.toasts.requiredRestock'));
            return;
        }

        const invalidLine = lines.find((line) => !Number.isInteger(line.quantity) || line.quantity <= 0);
        if (invalidLine) {
            toast.error(t('stock.toasts.invalidQuantity'));
            return;
        }

        try {
            setRestockSaving(true);
            await apiClient.post('/centre-stocks/restock_request/', {
                centre: selectedCentre,
                supplier: restockSupplier,
                lines,
                delivery_date: restockDeliveryDate,
                comment: restockComment,
            });
            toast.success(t('stock.toasts.restockSent'));
            handleCloseRestockDialog();
        } catch (error) {
            console.error('Error creating restock request:', error);
            const data = error?.response?.data || {};
            const detail = data.detail
                || data.quantity?.[0]
                || data.supplier?.[0]
                || data.consommable?.[0]
                || data.lines?.[0]
                || data.lines
                || data.delivery_date?.[0]
                || t('stock.toasts.restockCreateError');
            toast.error(detail);
        } finally {
            setRestockSaving(false);
        }
    };

    const fetchRestockRequests = async (row = restockRequestsRow, filters = {}) => {
        if (!row || !selectedCentre) return;
        const typeFilter = Object.prototype.hasOwnProperty.call(filters, 'type') ? filters.type : restockRequestsType;
        const statusFilter = Object.prototype.hasOwnProperty.call(filters, 'status') ? filters.status : restockRequestsStatus;
        const dateFromFilter = Object.prototype.hasOwnProperty.call(filters, 'dateFrom') ? filters.dateFrom : restockRequestsDateFrom;
        const dateToFilter = Object.prototype.hasOwnProperty.call(filters, 'dateTo') ? filters.dateTo : restockRequestsDateTo;

        const params = new URLSearchParams();
        params.set('centre_id', selectedCentre);
        params.set('consommable_id', row.consommable);
        if (typeFilter) params.set('order_type_group', typeFilter);
        if (statusFilter) params.set('status', statusFilter);
        if (dateFromFilter) params.set('date_from', dateFromFilter);
        if (dateToFilter) params.set('date_to', dateToFilter);

        try {
            setRestockRequestsLoading(true);
            const response = await apiClient.get(`/sessions/supplier_orders/?${params.toString()}`);
            const results = response.data?.results || [];
            setRestockRequests(Array.isArray(results) ? results : []);
        } catch (error) {
            console.error('Error fetching restock requests:', error);
            setRestockRequests([]);
            toast.error(t('stock.toasts.loadRequestsError'));
        } finally {
            setRestockRequestsLoading(false);
        }
    };

    const handleOpenRestockRequestsDialog = async (row) => {
        setRestockRequestsRow(row);
        setRestockRequestsType('');
        setRestockRequestsStatus('');
        setRestockRequestsDateFrom('');
        setRestockRequestsDateTo('');
        setRestockRequests([]);
        setRestockRequestsDialogOpen(true);

        const params = new URLSearchParams();
        params.set('centre_id', selectedCentre);
        params.set('consommable_id', row.consommable);

        try {
            setRestockRequestsLoading(true);
            const response = await apiClient.get(`/sessions/supplier_orders/?${params.toString()}`);
            const results = response.data?.results || [];
            setRestockRequests(Array.isArray(results) ? results : []);
        } catch (error) {
            console.error('Error fetching restock requests:', error);
            setRestockRequests([]);
            toast.error(t('stock.toasts.loadRequestsError'));
        } finally {
            setRestockRequestsLoading(false);
        }
    };

    const handleCloseRestockRequestsDialog = () => {
        setRestockRequestsDialogOpen(false);
        setRestockRequestsRow(null);
        setRestockRequests([]);
        setRestockRequestsType('');
        setRestockRequestsStatus('');
        setRestockRequestsDateFrom('');
        setRestockRequestsDateTo('');
        setSelectedRestockRequest(null);
    };

    const handleOpenRestockRequestDetail = (request) => {
        setSelectedRestockRequest(request);
    };

    const handleCloseRestockRequestDetail = () => {
        setSelectedRestockRequest(null);
    };

    const handleResetRestockRequestFilters = () => {
        setRestockRequestsType('');
        setRestockRequestsStatus('');
        setRestockRequestsDateFrom('');
        setRestockRequestsDateTo('');
        fetchRestockRequests(restockRequestsRow, { type: '', status: '', dateFrom: '', dateTo: '' });
    };

    const getDemandStatusLabel = (statusValue) => {
        if (!statusValue) return '-';
        return t(`demandes.status.${statusValue}`, { defaultValue: statusValue });
    };

    const getOrderTypeLabel = (request) => (
        request?.order_type_label
        || (request?.order_type === 'STOCK_RESTOCK'
            ? t('demandes.orderTypes.stockRestock')
            : t('demandes.orderTypes.session'))
    );

    const getRestockRequestQuantity = (request) => (
        (request?.lines || [])
            .filter((line) => String(line.consommable_id) === String(restockRequestsRow?.consommable))
            .reduce((total, line) => total + Number(line.quantity || 0), 0)
    );

    const formatDemandDate = (value) => {
        if (!value) return '-';
        const dt = new Date(value);
        if (Number.isNaN(dt.getTime())) return '-';
        return dt.toLocaleDateString(i18n.language);
    };

    const getDemandTimeline = (request) => ([
        { label: t('demandes.detail.createdAt'), value: request?.created_at },
        { label: t('demandes.detail.deliverBefore'), value: request?.delivery_date },
        { label: t('demandes.detail.acceptedAt'), value: request?.accepted_at },
        { label: t('demandes.detail.shippedAt'), value: request?.shipped_at },
        { label: t('demandes.detail.receivedAt'), value: request?.received_at },
        { label: t('demandes.detail.invoicedAt'), value: request?.invoiced_at },
    ].filter((item) => item.value));

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleStockPageChange = (page) => {
        const nextPage = Math.min(Math.max(page, 1), stockTotalPages);
        setStockCurrentPage(nextPage);
    };

    useEffect(() => {
        setStockCurrentPage(1);
    }, [selectedCentre, debouncedSearchTerm]);

    const stockTotalPages = Math.max(1, Math.ceil(stockRows.length / pageSize));
    const stockStartIndex = (stockCurrentPage - 1) * pageSize;
    const paginatedStockRows = stockRows.slice(stockStartIndex, stockStartIndex + pageSize);

    useEffect(() => {
        if (stockCurrentPage > stockTotalPages) {
            setStockCurrentPage(stockTotalPages);
        }
    }, [stockCurrentPage, stockTotalPages]);

    const handleEditClick = (item) => {
        setConsommableToEdit(item);
        setIsDialogOpen(true);
    };

    const handleAddClick = () => {
        setConsommableToEdit(null);
        setIsDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setIsDialogOpen(false);
        setConsommableToEdit(null);
    };

    const handleViewClick = (item) => {
        setConsommableToView(item);
        setIsViewDialogOpen(true);
    };

    const handleCloseViewDialog = () => {
        setIsViewDialogOpen(false);
        setConsommableToView(null);
    };

    const handleAssignClick = (item) => {
        setConsommableToAssign(item);
        setIsAssignDialogOpen(true);
    };

    const handleDelete = async (item) => {
        const toastId = toast.custom(
            'danger',
            <div style={{ textAlign: 'center' }}>
                <div style={{ marginBottom: '1rem', fontSize: '1.1rem', fontWeight: '600' }}>
                    {t('consommables.confirmDeleteTitle')}
                </div>
                <div style={{ marginBottom: '1rem', color: '#6b7280', fontSize: '0.9rem' }}>
                    <strong>{item?.name}</strong><br/>
                    {t('consommables.confirmDeleteMessage')}
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', marginTop: '1rem' }}>
                    <button
                        type="button"
                        className="toast-btn toast-btn-danger"
                        onClick={async () => {
                            toast.remove(toastId);
                            try {
                                await apiClient.delete(`/consommables/${item.id}/`);
                                fetchConsommables(currentPage);
                                toast.success(t('consommables.deleteSuccess'));
                            } catch (error) {
                                console.error('Error deleting consommable:', error);
                                toast.error(t('consommables.deleteError'));
                            }
                        }}
                    >
                        {t('common.delete')}
                    </button>
                    <button
                        type="button"
                        className="toast-btn"
                        onClick={() => toast.remove(toastId)}
                    >
                        {t('common.cancel', { defaultValue: 'Annuler' })}
                    </button>
                </div>
            </div>,
            { duration: null }
        );
    };

    const handleCloseAssignDialog = () => {
        setIsAssignDialogOpen(false);
        setConsommableToAssign(null);
    };

    if (loading) {
        return (
            <div className="page-loading">
                <div className="loader"></div>
                <span>{t('consommables.loading')}</span>
            </div>
        );
    }

    return (
        <div className="consommables-page">
            <div className="page-header-section">
                <div className="header-titles">
                    <h1 className="page-title">
                        <Package size={32} />
                        {t('consommables.title')}
                    </h1>
                    <p className="page-subtitle">{t('consommables.subtitle')}</p>
                </div>
                {canCreate('consommables') && (
                    <button onClick={handleAddClick} className="btn-add">
                        <Plus size={20} />
                        {t('consommables.add')}
                    </button>
                )}
            </div>

            <div className="consommables-tabs">
                <button
                    type="button"
                    className={`consommables-tab ${activeTab === 'catalog' ? 'active' : ''}`}
                    onClick={() => handleTabChange('catalog')}
                >
                    <Package size={16} />
                        {t('stock.tabs.catalog')}
                </button>
                {canViewStock && (
                    <button
                        type="button"
                        className={`consommables-tab ${activeTab === 'stock' ? 'active' : ''}`}
                        onClick={() => handleTabChange('stock')}
                    >
                        <Boxes size={16} />
                        {t('stock.tabs.stock')}
                    </button>
                )}
            </div>

            <div className="filters-section">
                <div className="search-bar">
                    <Search size={20} className="search-icon" />
                    <input
                        type="text"
                        placeholder={t('consommables.searchPlaceholder')}
                        value={searchTerm}
                        onChange={handleSearchChange}
                        onKeyDown={handleSearchKeyDown}
                        onBlur={handleSearchBlur}
                        className="search-input"
                    />
                    {searchTerm && (
                        <button 
                            className="search-clear-btn"
                            onClick={handleClearSearch}
                            title={t('common.clearSearch')}
                        >
                            <X size={16} />
                        </button>
                    )}
                </div>

                {activeTab === 'catalog' && (
                    <div className="filter-group">
                        <Filter size={18} />
                        <select
                            value={filterCategory}
                            onChange={handleFilterChange}
                            className="filter-select"
                        >
                            <option value="all">{t('consommables.filters.allTypes')}</option>
                            <option value="FIXE">{t('consommables.filters.fixed')}</option>
                            <option value="VARIABLE">{t('consommables.filters.variable')}</option>
                        </select>
                    </div>
                )}

                {activeTab === 'stock' && canViewStock && (
                    <div className="filter-group">
                        <Filter size={18} />
                        {centres.length > 1 ? (
                            <select
                                value={selectedCentre}
                                onChange={(e) => setSelectedCentre(e.target.value)}
                                className="filter-select"
                            >
                                <option value="">{t('stock.filters.selectCenter')}</option>
                                {centres.map((centre) => (
                                    <option key={centre.id} value={centre.id}>{centre.name}</option>
                                ))}
                            </select>
                        ) : (
                            <span className="centre-filter-label">
                                {centres[0]?.name || t('stock.filters.noCenter')}
                            </span>
                        )}
                    </div>
                )}

                {activeTab === 'stock' && canRequestRestock && (
                    <button
                        type="button"
                        className="btn-stock-restock"
                        onClick={() => handleOpenRestockDialog()}
                        disabled={!selectedCentre}
                    >
                        <Truck size={18} />
                        {t('stock.actions.restock')}
                    </button>
                )}
            </div>

            {activeTab === 'catalog' && (
            <div className="ui-fixed-list">
                <div className="ui-fixed-list-scroll overflow-auto">
                    <div className="table-container">
                        <table className={`data-table ${isMobile ? 'mobile-table' : ''}`}>
                            {isMobile ? (
                                <thead className="mobile-thead">
                                    <tr>
                                        <th className="mobile-name-col">{t('consommables.table.name')}</th>
                                        <th className="mobile-actions-col">{t('consommables.table.actions')}</th>
                                    </tr>
                                </thead>
                            ) : (
                                <thead>
                                    <tr>
                                        <th>{t('consommables.table.name')}</th>
                                        <th>{t('consommables.table.type')}</th>
                                        <th>{t('consommables.table.unitPrice')}</th>
                                        <th>{t('consommables.table.quantity')}</th>
                                        <th>{t('consommables.table.formations')}</th>
                                        <th>{t('consommables.table.supplier')}</th>
                                        <th className="actions-column">{t('consommables.table.actions')}</th>
                                    </tr>
                                </thead>
                            )}
                            <tbody>
                                {consommables.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" className="empty-state">
                                            <Package size={48} className="empty-icon" />
                                            <h3>{t('consommables.empty.title')}</h3>
                                            <p>{t('consommables.empty.text')}</p>
                                        </td>
                                    </tr>
                                ) : (
                                    consommables.map((item) => (
                                        isMobile ? (
                                            <tr key={item.id} className="mobile-row">
                                                <td className="mobile-name-cell">
                                                    <div className="mobile-consommable-name">{item.name}</div>
                                                </td>
                                                <td className="mobile-actions-cell">
                                                    <div className="mobile-actions-wrapper">
                                                        <button className="action-btn action-view" onClick={() => handleViewClick(item)} title={t('common.view')}><Eye size={16} /></button>
                                                        <button className="action-btn action-assign" onClick={() => handleAssignClick(item)} title={t('consommables.assignToFormations')}><Link2 size={16} /></button>
                                                        {canEdit('consommables') && (
                                                            <button className="action-btn action-edit" onClick={() => handleEditClick(item)} title={t('common.edit')}><Edit size={16} /></button>
                                                        )}
                                                        {canDelete('consommables') && (
                                                            <button className="action-btn action-delete" onClick={() => handleDelete(item)} title={t('common.delete')}><Trash2 size={16} /></button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : (
                                            <tr key={item.id}>
                                                <td className="name-cell">{item.name}</td>
                                                <td>
                                                    <span className={`type-badge type-${getDisplayType(item.type_consommable).toLowerCase()}`}>
                                                        {t(`consommables.types.${getDisplayType(item.type_consommable)}`)}
                                                    </span>
                                                </td>
                                                <td className="price-cell">
                                                    <CircleDollarSign size={14} />
                                                    {parseFloat(item.unit_price).toFixed(2)} {item.effective_devise || 'EUR'}
                                                </td>
                                                <td className="quantity-cell">
                                                    <div>
                                                        {formatConsommableRule(item, t)}
                                                    </div>
                                                    {item.is_per_day && (
                                                        <div className="per-day-badge" style={{ fontSize: '0.7rem', color: '#0369a1', marginTop: '0.25rem', fontWeight: 600 }}>
                                                            {t('consommables.perDay')}
                                                        </div>
                                                    )}
                                                </td>
                                                <td>{item.formations_details?.length > 0
                                                    ? item.formations_details.map(f => f.title).join(', ')
                                                    : <span className="unassigned">{t('consommables.unassigned')}</span>}
                                                </td>
                                                <td>{item.supplier_details?.name || '-'}</td>
                                                <td className="actions-cell">
                                                    <button className="action-btn action-view" onClick={() => handleViewClick(item)} title={t('common.view')}><Eye size={16} /></button>
                                                    <button className="action-btn action-assign" onClick={() => handleAssignClick(item)} title={t('consommables.assignToFormations')}><Link2 size={16} /></button>
                                                    {canEdit('consommables') && (
                                                        <button className="action-btn action-edit" onClick={() => handleEditClick(item)} title={t('common.edit')}><Edit size={16} /></button>
                                                    )}
                                                    {canDelete('consommables') && (
                                                        <button className="action-btn action-delete" onClick={() => handleDelete(item)} title={t('common.delete')}><Trash2 size={16} /></button>
                                                    )}
                                                </td>
                                            </tr>
                                        )
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="table-footer ui-fixed-list-footer">
                    <p className="results-count">
                        {t('consommables.resultsCount', { total: totalCount, currentPage, totalPages })}
                    </p>
                    <div className="pagination">
                        <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="pagination-btn"
                        >
                            {t('common.previous')}
                        </button>
                        <div className="page-numbers">
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                <button
                                    key={page}
                                    onClick={() => handlePageChange(page)}
                                    className={`page-number ${page === currentPage ? 'active' : ''}`}
                                >
                                    {page}
                                </button>
                            ))}
                        </div>
                        <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className="pagination-btn"
                        >
                            {t('common.next')}
                        </button>
                    </div>
                </div>
            </div>
            )}

            {activeTab === 'stock' && canViewStock && (
                <div className="ui-fixed-list">
                    <div className="ui-fixed-list-scroll overflow-auto">
                        <div className="table-container">
                            {!selectedCentre ? (
                                <div className="empty-state">
                                    <Boxes size={48} className="empty-icon" />
                                    <h3>{t('stock.empty.selectCenterTitle')}</h3>
                                    <p>{t('stock.empty.selectCenterText')}</p>
                                </div>
                            ) : stockLoading ? (
                                <div className="empty-state">
                                    <div className="loader"></div>
                                    <p>{t('stock.empty.loading')}</p>
                                </div>
                            ) : stockRows.length === 0 ? (
                                <div className="empty-state">
                                    <Boxes size={48} className="empty-icon" />
                                    <h3>{t('stock.empty.noSuppliesTitle')}</h3>
                                    <p>{t('stock.empty.noSuppliesText')}</p>
                                </div>
                            ) : isMobile ? (
                                <div className="stock-mobile-list">
                                    {paginatedStockRows.map((row) => (
                                        <div key={row.id} className="stock-mobile-item">
                                            <div className="stock-mobile-title">{row.consommable_name}</div>
                                            <div className="stock-mobile-meta">{row.supplier_name || '-'} - {row.centre_name || '-'}</div>
                                            <div className="stock-mobile-grid">
                                                <div>
                                                    <span>{t('stock.mobile.available')}</span>
                                                    <strong>{row.quantity_available}</strong>
                                                </div>
                                                <div>
                                                    <span>{t('stock.mobile.threshold')}</span>
                                                    <strong>{row.minimum_threshold}</strong>
                                                </div>
                                                <div>
                                                    <span>{t('stock.mobile.status')}</span>
                                                    <strong className={row.needs_restock ? 'text-danger' : 'text-success'}>
                                                        {row.needs_restock ? t('stock.status.needsRestock') : t('stock.status.ok')}
                                                    </strong>
                                                </div>
                                            </div>
                                            {canShowStockActions && (
                                                <div className="stock-action-buttons stock-action-buttons-mobile">
                                                    <div className="stock-action-row">
                                                        {canSetStock && (
                                                            <button type="button" className="action-btn action-edit" onClick={() => handleOpenStockDialog(row, 'SET')} title={t('stock.actions.edit')}>
                                                                <Edit size={16} />
                                                            </button>
                                                        )}
                                                        {canRequestRestock && (
                                                            <button type="button" className="action-btn action-assign" onClick={() => handleOpenRestockDialog(row)} title={t('stock.actions.restockLong')}>
                                                                <Truck size={16} />
                                                            </button>
                                                        )}
                                                        {canViewRestockRequests && (
                                                            <button type="button" className="action-btn action-view" onClick={() => handleOpenRestockRequestsDialog(row)} title={t('stock.actions.viewRestockRequests')}>
                                                                <ClipboardList size={16} />
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <table className="data-table stock-table">
                                    <thead>
                                        <tr>
                                            <th>{t('stock.table.consommable')}</th>
                                            <th>{t('stock.table.centre')}</th>
                                            <th>{t('stock.table.supplier')}</th>
                                            <th>{t('stock.table.available')}</th>
                                            <th>{t('stock.table.threshold')}</th>
                                            <th>{t('stock.table.status')}</th>
                                            <th>{t('stock.table.updatedAt')}</th>
                                            {canShowStockActions && <th className="actions-column">{t('common.actions')}</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginatedStockRows.map((row) => (
                                            <tr key={row.id}>
                                                <td className="name-cell">{row.consommable_name}</td>
                                                <td>{row.centre_name || '-'}</td>
                                                <td>{row.supplier_name || '-'}</td>
                                                <td className="stock-cell">
                                                    <span className={`stock-badge ${row.needs_restock ? 'stock-low' : ''}`}>
                                                        {row.quantity_available}
                                                    </span>
                                                </td>
                                                <td>{row.minimum_threshold}</td>
                                                <td>
                                                    <span className={`stock-status ${row.needs_restock ? 'stock-status-low' : 'stock-status-ok'}`}>
                                                        {row.needs_restock ? t('stock.status.needsRestock') : t('stock.status.ok')}
                                                    </span>
                                                </td>
                                                <td>{row.updated_at ? new Date(row.updated_at).toLocaleDateString(i18n.language) : '-'}</td>
                                                {canShowStockActions && (
                                                    <td className="actions-cell">
                                                        <div className="stock-action-buttons stock-action-buttons-table">
                                                            <div className="stock-action-row">
                                                                {canSetStock && (
                                                                    <button type="button" className="action-btn action-edit" onClick={() => handleOpenStockDialog(row, 'SET')} title={t('stock.actions.edit')}>
                                                                        <Edit size={16} />
                                                                    </button>
                                                                )}
                                                                {canRequestRestock && (
                                                                    <button type="button" className="action-btn action-assign" onClick={() => handleOpenRestockDialog(row)} title={t('stock.actions.restockLong')}>
                                                                        <Truck size={16} />
                                                                    </button>
                                                                )}
                                                                {canViewRestockRequests && (
                                                                    <button type="button" className="action-btn action-view" onClick={() => handleOpenRestockRequestsDialog(row)} title={t('stock.actions.viewRestockRequests')}>
                                                                        <ClipboardList size={16} />
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                    <div className="table-footer ui-fixed-list-footer">
                        <p className="results-count">
                            {t('stock.resultsCount', { total: stockRows.length, currentPage: stockCurrentPage, totalPages: stockTotalPages })}
                        </p>
                        <div className="pagination">
                            <button
                                onClick={() => handleStockPageChange(stockCurrentPage - 1)}
                                disabled={stockCurrentPage === 1}
                                className="pagination-btn"
                            >
                                {t('common.previous')}
                            </button>
                            <div className="page-numbers">
                                {Array.from({ length: stockTotalPages }, (_, i) => i + 1).map(page => (
                                    <button
                                        key={page}
                                        onClick={() => handleStockPageChange(page)}
                                        className={`page-number ${page === stockCurrentPage ? 'active' : ''}`}
                                    >
                                        {page}
                                    </button>
                                ))}
                            </div>
                            <button
                                onClick={() => handleStockPageChange(stockCurrentPage + 1)}
                                disabled={stockCurrentPage === stockTotalPages}
                                className="pagination-btn"
                            >
                                {t('common.next')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <AddConsommableDialog
                isOpen={isDialogOpen}
                onClose={handleCloseDialog}
                onConsommableAdded={handleConsommableAdded}
                consommableToEdit={consommableToEdit}
            />
            <ViewConsommableDialog
                isOpen={isViewDialogOpen}
                onClose={handleCloseViewDialog}
                consommable={consommableToView}
            />
            <AssignFormationsDialog
                isOpen={isAssignDialogOpen}
                onClose={handleCloseAssignDialog}
                onAssigned={fetchConsommables}
                consommable={consommableToAssign}
            />
            {stockDialogOpen && stockDialogRow && (
                <div className="stock-dialog-overlay" onMouseDown={handleCloseStockDialog}>
                    <form className="stock-dialog" onSubmit={handleSaveStockAdjustment} onMouseDown={(event) => event.stopPropagation()}>
                        <div className="stock-dialog-header">
                            <div>
                                <h2>
                                    {stockDialogMode === 'ADD'
                                        ? t('stock.actions.add')
                                        : stockDialogMode === 'REMOVE'
                                            ? t('stock.actions.remove')
                                            : t('stock.actions.edit')}
                                </h2>
                                <p>{stockDialogRow.consommable_name} - {stockDialogRow.centre_name || t('stock.table.centre')}</p>
                            </div>
                            <button type="button" className="stock-dialog-close" onClick={handleCloseStockDialog}>
                                <X size={18} />
                            </button>
                        </div>

                        <div className="stock-dialog-body">
                            <div className="stock-form-group">
                                <label>
                                    {stockDialogMode === 'ADD'
                                        ? t('stock.dialog.quantityToAdd')
                                        : stockDialogMode === 'REMOVE'
                                            ? t('stock.dialog.quantityToRemove')
                                            : t('stock.table.available')}
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    step="1"
                                    value={stockQuantity}
                                    onChange={(event) => setStockQuantity(event.target.value)}
                                    autoFocus
                                    required
                                />
                            </div>

                            <div className="stock-form-group">
                                <label>{t('stock.table.threshold')}</label>
                                <input
                                    type="number"
                                    min="0"
                                    step="1"
                                    value={stockThreshold}
                                    onChange={(event) => setStockThreshold(event.target.value)}
                                />
                            </div>

                            <div className="stock-form-group stock-form-group-full">
                                <label>{t('stock.dialog.comment')}</label>
                                <textarea
                                    rows="3"
                                    value={stockComment}
                                    onChange={(event) => setStockComment(event.target.value)}
                                    placeholder={t('stock.dialog.stockCommentPlaceholder')}
                                />
                            </div>
                        </div>

                        <div className="stock-dialog-footer">
                            <button type="button" className="btn-secondary" onClick={handleCloseStockDialog} disabled={stockSaving}>
                                {t('common.cancel')}
                            </button>
                            <button type="submit" className="btn-primary" disabled={stockSaving}>
                                {stockSaving ? t('common.saving') : t('common.save')}
                            </button>
                        </div>
                    </form>
                </div>
            )}
            {restockRequestsDialogOpen && restockRequestsRow && (
                <div className="stock-dialog-overlay" onMouseDown={handleCloseRestockRequestsDialog}>
                    <div className="stock-dialog stock-requests-dialog" onMouseDown={(event) => event.stopPropagation()}>
                        <div className="stock-dialog-header">
                            <div>
                                <h2>{t('stock.requests.title')}</h2>
                                <p>{restockRequestsRow.consommable_name} - {restockRequestsRow.centre_name || t('stock.table.centre')}</p>
                            </div>
                            <button type="button" className="stock-dialog-close" onClick={handleCloseRestockRequestsDialog}>
                                <X size={18} />
                            </button>
                        </div>

                        <div className="stock-requests-filters">
                            <div className="stock-form-group">
                                <label>{t('stock.requests.typeFilter')}</label>
                                <select
                                    value={restockRequestsType}
                                    onChange={(event) => setRestockRequestsType(event.target.value)}
                                >
                                    <option value="">{t('stock.requests.allTypes')}</option>
                                    <option value="STOCK_RESTOCK">{t('demandes.orderTypes.stockRestock')}</option>
                                    <option value="SESSION">{t('demandes.orderTypes.session')}</option>
                                </select>
                            </div>
                            <div className="stock-form-group">
                                <label>{t('stock.requests.statusFilter')}</label>
                                <select
                                    value={restockRequestsStatus}
                                    onChange={(event) => setRestockRequestsStatus(event.target.value)}
                                >
                                    <option value="">{t('stock.requests.allStatuses')}</option>
                                    {['NEW', 'ACCEPTED', 'SHIPPED', 'RECEIVED', 'INVOICED', 'CANCELLED'].map((statusValue) => (
                                        <option key={statusValue} value={statusValue}>{getDemandStatusLabel(statusValue)}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="stock-form-group">
                                <label>{t('stock.requests.dateFrom')}</label>
                                <input
                                    type="date"
                                    value={restockRequestsDateFrom}
                                    onChange={(event) => setRestockRequestsDateFrom(event.target.value)}
                                />
                            </div>
                            <div className="stock-form-group">
                                <label>{t('stock.requests.dateTo')}</label>
                                <input
                                    type="date"
                                    value={restockRequestsDateTo}
                                    onChange={(event) => setRestockRequestsDateTo(event.target.value)}
                                />
                            </div>
                            <div className="stock-requests-filter-actions">
                                <button type="button" className="btn-secondary" onClick={handleResetRestockRequestFilters}>
                                    {t('stock.requests.resetFilters')}
                                </button>
                                <button type="button" className="btn-primary" onClick={() => fetchRestockRequests()}>
                                    {t('stock.requests.applyFilters')}
                                </button>
                            </div>
                        </div>

                        <div className="stock-requests-body">
                            {restockRequestsLoading ? (
                                <div className="empty-state compact">
                                    <div className="loader"></div>
                                    <p>{t('stock.requests.loading')}</p>
                                </div>
                            ) : restockRequests.length === 0 ? (
                                <div className="empty-state compact">
                                    <ClipboardList size={36} className="empty-icon" />
                                    <p>{t('stock.requests.empty')}</p>
                                </div>
                            ) : (
                                <div className="stock-requests-list">
                                    {restockRequests.map((request) => (
                                        <div key={request.order_id} className="stock-request-card">
                                            <div className="stock-request-main">
                                                <div>
                                                    <strong>{request.supplier_name || '-'}</strong>
                                                    <span>{t('stock.requests.createdOn', { date: formatDemandDate(request.created_at) })}</span>
                                                </div>
                                                <span className={`stock-request-status stock-request-status-${(request.status || '').toLowerCase()}`}>
                                                    {getDemandStatusLabel(request.status)}
                                                </span>
                                                <span className={`stock-request-type stock-request-type-${(request.order_type || 'SESSION').toLowerCase()}`}>
                                                    {getOrderTypeLabel(request)}
                                                </span>
                                            </div>
                                            <div className="stock-request-meta">
                                                <span>{t('stock.requests.quantity')}: <strong>{getRestockRequestQuantity(request)}</strong></span>
                                                <span>{t('stock.requests.deliveryDate')}: <strong>{formatDemandDate(request.delivery_date)}</strong></span>
                                                <span>{t('stock.requests.total')}: <strong>{request.total_devis || '0'}</strong></span>
                                            </div>
                                            <div className="stock-request-lines">
                                                {(request.lines || []).map((line) => (
                                                    <span key={line.id}>{line.consommable_name} x {line.quantity}</span>
                                                ))}
                                            </div>
                                            <div className="stock-request-actions">
                                                <button type="button" className="btn-secondary" onClick={() => handleOpenRestockRequestDetail(request)}>
                                                    {t('stock.requests.openDetail')}
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            {selectedRestockRequest && (
                <div className="stock-dialog-overlay stock-detail-overlay" onMouseDown={handleCloseRestockRequestDetail}>
                    <div className="stock-dialog stock-request-detail-dialog" onMouseDown={(event) => event.stopPropagation()}>
                        <div className="stock-dialog-header">
                            <div>
                                <h2>{t('stock.requests.detailTitle')}</h2>
                                <p>{selectedRestockRequest.supplier_name || '-'} - {getDemandStatusLabel(selectedRestockRequest.status)}</p>
                            </div>
                            <button type="button" className="stock-dialog-close" onClick={handleCloseRestockRequestDetail}>
                                <X size={18} />
                            </button>
                        </div>

                        <div className="stock-request-detail-body">
                            <div className="stock-request-detail-section">
                                <h3>{t('stock.requests.requestInfo')}</h3>
                                <div className="stock-request-detail-grid">
                                    <div>
                                        <span>{t('demandes.table.type')}</span>
                                        <strong>{getOrderTypeLabel(selectedRestockRequest)}</strong>
                                    </div>
                                    <div>
                                        <span>{t('demandes.table.supplier')}</span>
                                        <strong>{selectedRestockRequest.supplier_name || '-'}</strong>
                                    </div>
                                    <div>
                                        <span>{t('stock.requests.requestedBy')}</span>
                                        <strong>{selectedRestockRequest.created_by_name || '-'}</strong>
                                    </div>
                                    <div>
                                        <span>{t('stock.requests.status')}</span>
                                        <strong>{getDemandStatusLabel(selectedRestockRequest.status)}</strong>
                                    </div>
                                    <div>
                                        <span>{t('demandes.detail.centre')}</span>
                                        <strong>{selectedRestockRequest.centre_name || '-'}</strong>
                                    </div>
                                    <div>
                                        <span>{t('stock.requests.total')}</span>
                                        <strong>{selectedRestockRequest.total_devis || '0'} {selectedRestockRequest.supplier_devise || selectedRestockRequest.centre_devise || ''}</strong>
                                    </div>
                                </div>
                            </div>

                            <div className="stock-request-detail-section">
                                <h3>{t('stock.requests.sessionInfo')}</h3>
                                <div className="stock-request-detail-grid">
                                    <div>
                                        <span>{t('sessions.table.formation')}</span>
                                        <strong>{selectedRestockRequest.formation_title || '-'}</strong>
                                    </div>
                                    <div>
                                        <span>{t('demandes.detail.trainer')}</span>
                                        <strong>{selectedRestockRequest.trainer_name || '-'}</strong>
                                    </div>
                                    <div>
                                        <span>{t('stock.requests.version')}</span>
                                        <strong>{selectedRestockRequest.version || 1}</strong>
                                    </div>
                                    <div>
                                        <span>{t('demandes.detail.createdAt')}</span>
                                        <strong>{formatDemandDate(selectedRestockRequest.created_at)}</strong>
                                    </div>
                                </div>
                            </div>

                            <div className="stock-request-detail-section">
                                <h3>{t('stock.requests.timeline')}</h3>
                                <div className="stock-request-timeline">
                                    {getDemandTimeline(selectedRestockRequest).map((item) => (
                                        <div key={item.label}>
                                            <span>{item.label}</span>
                                            <strong>{formatDemandDate(item.value)}</strong>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="stock-request-detail-section">
                                <h3>{t('stock.requests.lines')}</h3>
                                <div className="stock-request-detail-lines">
                                    {(selectedRestockRequest.lines || []).map((line) => (
                                        <div key={line.id} className="stock-request-detail-line">
                                            <div>
                                                <strong>{line.consommable_name}</strong>
                                                {line.comment && <span>{line.comment}</span>}
                                            </div>
                                            <span>{t('demandes.detail.quantityShort')}: <strong>{line.quantity}</strong></span>
                                            <span>{t('demandes.detail.price')}: <strong>{line.unit_price}</strong></span>
                                            <span>{t('stock.requests.total')}: <strong>{line.line_total}</strong></span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="stock-dialog-footer">
                            <button type="button" className="btn-secondary" onClick={handleCloseRestockRequestDetail}>
                                {t('common.close')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {restockDialogOpen && (
                <div className="stock-dialog-overlay" onMouseDown={handleCloseRestockDialog}>
                    <form className="stock-dialog" onSubmit={handleSaveRestockRequest} onMouseDown={(event) => event.stopPropagation()}>
                        <div className="stock-dialog-header">
                            <div>
                                <h2>{t('stock.dialog.restockTitle')}</h2>
                                <p>{centres.find((centre) => String(centre.id) === String(selectedCentre))?.name || t('stock.table.centre')}</p>
                            </div>
                            <button type="button" className="stock-dialog-close" onClick={handleCloseRestockDialog}>
                                <X size={18} />
                            </button>
                        </div>

                        <div className="stock-dialog-body">
                            <div className="stock-form-group">
                                <label>{t('stock.dialog.supplier')}</label>
                                <select
                                    value={restockSupplier}
                                    onChange={(event) => {
                                        setRestockSupplier(event.target.value);
                                        setRestockLines({});
                                    }}
                                    required
                                    disabled={suppliersLoading}
                                >
                                    <option value="">{suppliersLoading ? t('stock.dialog.supplierLoading') : t('stock.dialog.supplierPlaceholder')}</option>
                                    {suppliers.map((supplier) => (
                                        <option key={supplier.id} value={supplier.id}>{supplier.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="stock-form-group">
                                <label>{t('stock.dialog.deliveryDate')}</label>
                                <input
                                    type="date"
                                    value={restockDeliveryDate}
                                    onChange={(event) => setRestockDeliveryDate(event.target.value)}
                                    required
                                />
                            </div>

                            <div className="stock-form-group stock-form-group-full">
                                <label>{t('stock.dialog.consommable')}</label>
                                <div className="restock-lines-table">
                                    <div className="restock-lines-header">
                                        <span>{t('stock.dialog.consommable')}</span>
                                        <span>{t('stock.dialog.currentStock')}</span>
                                        <span>{t('stock.dialog.quantity')}</span>
                                    </div>
                                    {!restockSupplier ? (
                                        <div className="restock-lines-empty">{t('stock.dialog.supplierPlaceholder')}</div>
                                    ) : getRestockConsommableOptions().length === 0 ? (
                                        <div className="restock-lines-empty">{t('stock.dialog.consommablePlaceholder')}</div>
                                    ) : (
                                        getRestockConsommableOptions().map((row) => {
                                            const lineState = restockLines[String(row.consommable)] || {};
                                            return (
                                                <div key={row.consommable} className="restock-line-row">
                                                    <label className="restock-line-name">
                                                        <input
                                                            type="checkbox"
                                                            checked={Boolean(lineState.selected)}
                                                            onChange={(event) => updateRestockLine(String(row.consommable), { selected: event.target.checked })}
                                                        />
                                                        <span>{row.consommable_name}</span>
                                                    </label>
                                                    <span className="restock-line-stock">{row.quantity_available}</span>
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        step="1"
                                                        value={lineState.quantity || ''}
                                                        onChange={(event) => updateRestockLine(String(row.consommable), { quantity: event.target.value, selected: true })}
                                                        disabled={!lineState.selected}
                                                    />
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </div>

                            <div className="stock-form-group stock-form-group-full">
                                <label>{t('stock.dialog.comment')}</label>
                                <textarea
                                    rows="3"
                                    value={restockComment}
                                    onChange={(event) => setRestockComment(event.target.value)}
                                    placeholder={t('stock.dialog.commentPlaceholder')}
                                />
                            </div>
                        </div>

                        <div className="stock-dialog-footer">
                            <button type="button" className="btn-secondary" onClick={handleCloseRestockDialog} disabled={restockSaving}>
                                {t('common.cancel')}
                            </button>
                            <button type="submit" className="btn-primary" disabled={restockSaving}>
                                {restockSaving ? t('stock.dialog.sending') : t('stock.dialog.send')}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
};

export default ConsommablesPage;
